import { type Dispatch, type MutableRefObject, type SetStateAction, useEffect, useRef } from 'react'
import type { Guide } from '@/lib/guides/types/guide'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type LayoutSnapLines,
  type SmartGuideLine,
  computeSmartGuides as computeSmartGuidesForCreation,
} from '@/lib/guides/smart-guides'

/**
 * Live smart guides for the shape being drag-created: painted against every other visible shape
 * (and the layout lines, when snapping to guides) while its preview moves, cleared once it ends.
 */
export function useDragCreateSmartGuides({
  internalShapeManagerRef,
  dragPreviewShape,
  effectiveSmartGuides,
  snapGuideDistance,
  snapGuides,
  snapToGuides,
  layoutSnapLinesForDragCreateRef,
  setActiveSmartGuides,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  dragPreviewShape: ShapeData | null
  effectiveSmartGuides: boolean
  snapGuideDistance: number
  snapGuides: Guide[]
  snapToGuides: boolean
  layoutSnapLinesForDragCreateRef: MutableRefObject<LayoutSnapLines>
  setActiveSmartGuides: Dispatch<SetStateAction<SmartGuideLine[]>>
}) {
  const dragCreationSmartGuidesActive = useRef(false)

  useEffect(() => {
    if (!dragPreviewShape || !effectiveSmartGuides) {
      if (dragCreationSmartGuidesActive.current) {
        setActiveSmartGuides([])
        dragCreationSmartGuidesActive.current = false
      }

      return
    }

    const mgr = internalShapeManagerRef.current

    if (!mgr) return

    const allShapes = mgr.getShapes()
    const otherBounds = allShapes
      .filter((s) => !s.hidden)
      .map((s) => ({ x: s.x, y: s.y, width: s.width, height: s.height }))
    const movingBounds = {
      x: dragPreviewShape.x,
      y: dragPreviewShape.y,
      width: dragPreviewShape.width,
      height: dragPreviewShape.height,
    }

    const result = computeSmartGuidesForCreation(
      movingBounds,
      otherBounds,
      snapGuideDistance,
      snapToGuides ? layoutSnapLinesForDragCreateRef.current : undefined
    )

    setActiveSmartGuides(result.guides)
    dragCreationSmartGuidesActive.current = true
  }, [
    dragPreviewShape,
    effectiveSmartGuides,
    snapGuideDistance,
    snapGuides,
    snapToGuides,
    setActiveSmartGuides,
    internalShapeManagerRef,
    layoutSnapLinesForDragCreateRef,
  ])
}
